import { useCallback, useMemo, useState } from "react";
import { FadeModal } from "../../../../components/modal/fade-modal";
import { Divider } from "semantic-ui-react";
import { twMerge } from "tailwind-merge";
import {
  AcoustiGuideQuestionnaireData,
  AgeType,
  PriceWillingnessType,
  PreUsageTroubleType,
  ServiceDiscoveryType,
  StarRatingType,
  UsageReasonType,
  VisitCountType,
} from "./acousti-guide-questionnaire-data";

type QuestionnaireModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AcoustiGuideQuestionnaireData) => void;
};

const serviceDiscoveryOptions: ServiceDiscoveryType[] = [
  "来館して知った",
  "みんぱくのHP又はSNS",
  "Acoustiguide JapanのHP又はSNS",
  "ネットニュース",
  "その他",
];

const usageReasonOptions: UsageReasonType[] = [
  "事前にサービスを知って興味を持ったから",
  "新しい体験をしてみたかったから",
  "その他",
];

const visitCountOptions: VisitCountType[] = [
  "はじめて(1回目)",
  "2〜4回目",
  "5〜9回目",
  "10回目以上",
];

const preUsageTroubleOptions: PreUsageTroubleType[] = [
  "特になかった",
  "利用方法がわかりづらかった",
  "お金がかかるかと思った",
  "事前予約が必要かと思った",
  "その他",
];

const ages: AgeType[] = [
  "10代",
  "20代",
  "30代",
  "40代",
  "50代",
  "60代",
  "70歳以上",
];

const priceWillingnessOptions: PriceWillingnessType[] = [
  "無料でなければ利用しない",
  "500円",
  "1000円",
  "1500円",
  "2000円",
  "その他",
];

const StarRating = ({
  value,
  onChange,
}: {
  value: StarRatingType | null;
  onChange: (v: StarRatingType) => void;
}) => (
  <div className="flex gap-2">
    {([1, 2, 3, 4, 5] as StarRatingType[]).map((star) => (
      <button
        key={star}
        onClick={() => onChange(star)}
        className="text-5xl transition-all duration-100 active:scale-95"
        type="button"
      >
        <span className={value != null && star <= value ? "text-yellow-400" : "text-gray-300"}>
          ★
        </span>
      </button>
    ))}
  </div>
);

const RadioOption = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-3 py-2 w-full text-left active:opacity-70"
  >
    <span
      className={twMerge(
        "flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center",
        selected ? "border-[#bb5730]" : "border-gray-400"
      )}
    >
      {selected && <span className="w-3.5 h-3.5 rounded-full bg-[#bb5730]" />}
    </span>
    <span className="text-[18px]">{label}</span>
  </button>
);

const CheckboxOption = ({
  label,
  checked,
  onClick,
  disabled,
}: {
  label: string;
  checked: boolean;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={twMerge(
      "flex items-center gap-3 py-2 w-full text-left active:opacity-70",
      disabled && "opacity-40 pointer-events-none"
    )}
  >
    <span
      className={twMerge(
        "flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center",
        checked ? "border-[#bb5730] bg-[#bb5730]" : "border-gray-400"
      )}
    >
      {checked && (
        <svg className="w-4 h-4 text-white" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </span>
    <span className="text-[18px]">{label}</span>
  </button>
);

export const QuestionnaireFormModal = (props: QuestionnaireModalProps) => {
  const [starRating, setStarRating] = useState<StarRatingType | null>(null);
  const [serviceDiscovery, setServiceDiscovery] = useState<ServiceDiscoveryType | null>(null);
  const [serviceDiscoveryOther, setServiceDiscoveryOther] = useState("");
  const [usageReason, setUsageReason] = useState<UsageReasonType | null>(null);
  const [usageReasonOther, setUsageReasonOther] = useState("");
  const [visitCount, setVisitCount] = useState<VisitCountType | null>(null);
  const [preUsageTroubles, setPreUsageTroubles] = useState<PreUsageTroubleType[]>([]);
  const [preUsageTroublesOther, setPreUsageTroublesOther] = useState("");
  const [age, setAge] = useState<AgeType | null>(null);
  const [priceWillingness, setPriceWillingness] = useState<PriceWillingnessType | null>(null);
  const [priceWillingnessOther, setPriceWillingnessOther] = useState("");
  const [feedback, setFeedback] = useState("");
  const [otherComments, setOtherComments] = useState("");

  const disabledSubmitButton = useMemo(
    () =>
      starRating == null ||
      serviceDiscovery == null ||
      (serviceDiscovery === "その他" && serviceDiscoveryOther.trim().length === 0) ||
      usageReason == null ||
      (usageReason === "その他" && usageReasonOther.trim().length === 0) ||
      visitCount == null ||
      preUsageTroubles.length === 0 ||
      (preUsageTroubles.includes("その他") && preUsageTroublesOther.trim().length === 0) ||
      age == null ||
      priceWillingness == null ||
      (priceWillingness === "その他" && priceWillingnessOther.trim().length === 0),
    [
      starRating,
      serviceDiscovery,
      serviceDiscoveryOther,
      usageReason,
      usageReasonOther,
      visitCount,
      preUsageTroubles,
      preUsageTroublesOther,
      age,
      priceWillingness,
      priceWillingnessOther,
    ]
  );

  const handleSubmit = useCallback(() => {
    props.onSubmit({
      star_rating: starRating,
      service_discovery: serviceDiscovery,
      service_discovery_other: serviceDiscovery === "その他" ? serviceDiscoveryOther : undefined,
      usage_reason: usageReason,
      usage_reason_other: usageReason === "その他" ? usageReasonOther : undefined,
      visit_count: visitCount,
      pre_usage_troubles: preUsageTroubles,
      pre_usage_troubles_other: preUsageTroubles.includes("その他") ? preUsageTroublesOther : undefined,
      age: age,
      price_willingness: priceWillingness,
      price_willingness_other: priceWillingness === "その他" ? priceWillingnessOther : undefined,
      feedback: feedback.trim() || undefined,
      other_comments: otherComments.trim() || undefined,
    } as AcoustiGuideQuestionnaireData);
  }, [
    starRating,
    serviceDiscovery,
    serviceDiscoveryOther,
    usageReason,
    usageReasonOther,
    visitCount,
    preUsageTroubles,
    preUsageTroublesOther,
    age,
    priceWillingness,
    priceWillingnessOther,
    feedback,
    otherComments,
    props,
  ]);

  const togglePreUsageTrouble = useCallback(
    (trouble: PreUsageTroubleType) => {
      setPreUsageTroubles((prev) => {
        if (trouble === "特になかった") {
          return prev.includes("特になかった") ? [] : ["特になかった"];
        }
        const next = prev.includes(trouble)
          ? prev.filter((t) => t !== trouble)
          : [...prev.filter((t) => t !== "特になかった"), trouble];
        return next;
      });
    },
    []
  );

  const textareaClass =
    "block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-[#bb5730] sm:text-sm/6";

  return (
    <FadeModal isOpen={props.open} onClose={props.onClose}>
      <div
        className={twMerge(
          "relative px-3 py-5 flex flex-col gap-12 overflow-y-auto hidden-scrollbar h-[80vh]"
        )}
      >
        <div
          className={"absolute top-0 right-2 w-10 h-10"}
          onClick={props.onClose}
        >
          <img src="/minpaku-demo/icons/close.svg" className="w-full" />
        </div>
        <div className={"w-full flex justify-center text-4xl font-bold"}>
          アンケート
        </div>

        {/* Q1: 星評価 */}
        <div>
          <div className={"text-2xl font-bold"}>みんぱく音声ガイドの評価をお願いします</div>
          <Divider />
          <div className="flex justify-center">
            <StarRating value={starRating} onChange={setStarRating} />
          </div>
        </div>

        {/* Q2: きっかけ */}
        <RadioSelectArea
          title="このサービスを知ったきっかけを教えてください"
          options={serviceDiscoveryOptions}
          value={serviceDiscovery}
          onSelect={setServiceDiscovery}
        />
        {serviceDiscovery === "その他" && (
          <OtherTextInput value={serviceDiscoveryOther} onChange={setServiceDiscoveryOther} />
        )}

        {/* Q3: 理由 */}
        <RadioSelectArea
          title="今回ご利用いただいた理由を教えてください"
          options={usageReasonOptions}
          value={usageReason}
          onSelect={setUsageReason}
        />
        {usageReason === "その他" && (
          <OtherTextInput value={usageReasonOther} onChange={setUsageReasonOther} />
        )}

        {/* Q4: 来館回数 */}
        <RadioSelectArea
          title="民博に来られたのは何回目ですか"
          options={visitCountOptions}
          value={visitCount}
          onSelect={setVisitCount}
        />

        {/* Q5: 利用前の困りごと（複数選択） */}
        <div>
          <div className={"text-2xl font-bold"}>
            利用前に、わからなかったこと、困ったことはありますか？
          </div>
          <Divider />
          <div className="flex flex-col">
            {preUsageTroubleOptions.map((option) => (
              <CheckboxOption
                key={option}
                label={option}
                checked={preUsageTroubles.includes(option)}
                onClick={() => togglePreUsageTrouble(option)}
                disabled={option !== "特になかった" && preUsageTroubles.includes("特になかった")}
              />
            ))}
          </div>
          {preUsageTroubles.includes("その他") && (
            <div className="mt-2">
              <OtherTextInput value={preUsageTroublesOther} onChange={setPreUsageTroublesOther} />
            </div>
          )}
        </div>

        {/* Q6: 年齢 */}
        <RadioSelectArea
          title="あなたのご年齢を教えてください"
          options={ages}
          value={age}
          onSelect={setAge}
        />

        {/* Q7: 価格 */}
        <RadioSelectArea
          title="このサービスが有料になった場合、いくらであれば払いますか"
          options={priceWillingnessOptions}
          value={priceWillingness}
          onSelect={setPriceWillingness}
        />
        {priceWillingness === "その他" && (
          <OtherTextInput value={priceWillingnessOther} onChange={setPriceWillingnessOther} />
        )}

        {/* Q8: 自由記述 */}
        <div>
          <div className={"text-2xl font-bold"}>
            みんぱく音声ガイドを使ってみて気になったこと、感想、ご要望等
          </div>
          <Divider />
          <textarea
            rows={4}
            className={textareaClass}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
        </div>

        {/* Q9: 自由記述 */}
        <div>
          <div className={"text-2xl font-bold"}>
            その他、利用してみて気になったこと、感想、ご要望等
          </div>
          <Divider />
          <textarea
            rows={4}
            className={textareaClass}
            value={otherComments}
            onChange={(e) => setOtherComments(e.target.value)}
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={disabledSubmitButton}
          className={twMerge(
            "flex-shrink-0 rounded-[10px] border-[#bb5730] border-4 h-[44px] w-full text-[20px] font-bold text-[#bb5730] mt-8 mb-4",
            !disabledSubmitButton ? "border-[#bb5730]" : "border-grey",
            !disabledSubmitButton ? "text-[#bb5730]" : "text-gray-400"
          )}
        >
          回答
        </button>
      </div>
    </FadeModal>
  );
};

type RadioSelectAreaProps<T extends string> = {
  title: string;
  options: T[];
  value: T | null;
  onSelect: (v: T) => void;
};

const RadioSelectArea = <T extends string>(props: RadioSelectAreaProps<T>) => (
  <div>
    <div className={"text-2xl font-bold"}>{props.title}</div>
    <Divider />
    <div className="flex flex-col">
      {props.options.map((x) => (
        <RadioOption
          key={x}
          label={x}
          selected={x === props.value}
          onClick={() => props.onSelect(x)}
        />
      ))}
    </div>
  </div>
);

const OtherTextInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <div className="-mt-8">
    <textarea
      rows={2}
      className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-[#bb5730] sm:text-sm/6"
      placeholder="具体的に教えてください"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);
